import { PRIORITEIT_OPTIONS, KLEUR_OPTIONS } from "../../../utils/constants";
import { getEffectiveGebrekColor } from "../../../utils/helpers";

import {
  delete_media,
  report_situatie,
  upload_image,
  addGebrekMedia,
} from "../../../functions/api";
import Swal from "sweetalert2";
import GevaarlijkeSituatieSection from "./GevaarlijkeSituatieSection";
import LocatiesSection from "./LocationSection";
import GebrekHeader from "./GebrekHeader";
import MediaSection from "./MediaSection";
import DetailsSection from "./DetailsSection";

const GebrekItem = ({
  customerName,
  objectName,
  gebrek,
  index,
  isCollapsed,
  uploadError,
  gsUploadErrors = {},
  newLocationInput,
  copiedLocationsData,
  gebrekRef,
  onToggleCollapse,
  onDelete,
  onChange,
  onLocationInputChange,
  onAddLocation,
  onCreateNewWithLocations,
  onCopyLocations,
  onPasteLocations,
  onUpdateGebrek,
  onSetUploadError,
  onSetGsUploadError,
  onCopyEntireGebrek,
  onEditLocation,
  onDeleteLocation,
}) => {
  const title = (() => {
    // Check if gebrek has a color (either in kleur or gebrekcode.naam)
    const colorName = gebrek.kleur || gebrek.gebrekcode?.naam;
    if (colorName) {
      return `Gebrek ${index + 1} (${colorName})`;
    }
    // If no color but gebrek is saved (has an ID that's not temp), show "Geen kleur"
    if (!gebrek.gebrekid.startsWith("temp-")) {
      return `Gebrek ${index + 1} (Geen kleur)`;
    }
    // New unsaved gebrek
    return `Nieuw Gebrek (Nog niet opgeslagen)`;
  })();
  const effectiveColor = getEffectiveGebrekColor(gebrek);

  const handleMediaUploadInternal = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    onSetUploadError(gebrek.gebrekid, null);

    let updatedMedia = [...(gebrek.media || [])];
    let errorOccurred = false;

    for (const file of files) {
      try {
        const response = await upload_image(file);

        if (response && response.response) {
          const mediaItem = response.response;
          updatedMedia.push({
            mediaid: mediaItem.mediaid,
            url: mediaItem.url,
            naam: mediaItem.naam,
            type: file.type,
          });

          if (mediaItem.mediaid) {
            const gebrekMedia = await addGebrekMedia({
              mediaid: mediaItem.mediaid,
              gebrekid: gebrek.gebrekid,
            });

            if (gebrekMedia && gebrekMedia.response) {
              updatedMedia.push({
                ...mediaItem,
                gebrekmediaid: gebrekMedia.response.gebrekmediaid,
              });
            }
          }
        } else {
          throw new Error("Upload response did not contain valid media data.");
        }
      } catch (uploadErr) {
        console.error("Upload failed for file:", file.name, uploadErr);
        onSetUploadError(
          gebrek.gebrekid,
          `Uploaden van ${file.name} mislukt: ${uploadErr.message}`
        );
        errorOccurred = true;
      }
    }

    if (!errorOccurred || updatedMedia.length > (gebrek.media?.length || 0)) {
      onUpdateGebrek(index, { ...gebrek, media: updatedMedia });
    }
    event.target.value = null;
  };

  const deleteMediaItem = (mediaId) => {
    Swal.fire({
      title: "Weet je het zeker?",
      text: "U staat op het punt dit media item te verwijderen.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Ja, verwijder!",
      cancelButtonText: "Annuleren",
      reverseButtons: true,
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await delete_media(mediaId);
          if (response && response.ok) {
            const updatedMedia = (gebrek.media || []).filter(
              (mediaItem) => mediaItem.gebrekmediaid !== mediaId
            );
            onUpdateGebrek(index, { ...gebrek, media: updatedMedia });

            Swal.fire({
              title: "Verwijderd!",
              text: "Het media item is succesvol verwijderd.",
              icon: "success",
              timer: 1000,
              showCloseButton: true,
            });
          } else {
            throw new Error(response?.message || "Kon media niet verwijderen.");
          }
        } catch (error) {
          console.error("Failed to delete media:", error);
          Swal.fire({
            icon: "error",
            title: "Verwijderen Mislukt",
            text: `Er is een fout opgetreden: ${error.message}`,
          });
        }
      }
    });
  };

  const handleTekstBiebOmschrijving = (item) => {
    onChange(index, "omschrijving", item.omschrijving);
    const huidigeToelichting = (gebrek.toelichting ?? "").trim();
    if (huidigeToelichting === "") {
      onChange(index, "toelichting", item.toelichting);
    }
  };

  return (
    <div
      ref={gebrekRef}
      className="flex flex-col w-full border rounded-md mb-4 shadow-sm bg-white"
    >
      {/* Header */}
      <GebrekHeader
        gebrek={gebrek}
        title={title}
        effectiveColor={effectiveColor}
        isCollapsed={isCollapsed}
        onToggleCollapse={onToggleCollapse}
        onCopyEntireGebrek={onCopyEntireGebrek}
        onCreateNewWithLocations={onCreateNewWithLocations}
        onDelete={onDelete}
      />

      {/* Collapsible Details Section */}
      {!isCollapsed && (
        <div id={`gebrek-details-${gebrek.gebrekid}`} className="p-4 space-y-4">
          {/* Details section */}
          <DetailsSection
            gebrek={gebrek}
            index={index}
            PRIORITEIT_OPTIONS={PRIORITEIT_OPTIONS}
            KLEUR_OPTIONS={KLEUR_OPTIONS}
            onChange={onChange}
            handleTekstBiebOmschrijving={handleTekstBiebOmschrijving}
          />
          {/* End details section */}

          {/* --- Locaties Section --- */}
          <LocatiesSection
            locaties={gebrek.locaties}
            newLocationInput={newLocationInput}
            copiedLocationsData={copiedLocationsData}
            gebrekId={gebrek.gebrekid}
            onLocationInputChange={onLocationInputChange}
            onAddLocation={(gebrekId) => onAddLocation(index, gebrekId)}
            onCopyLocations={onCopyLocations}
            onPasteLocations={(gebrekId) => onPasteLocations(index, gebrekId)}
            onEditLocation={onEditLocation}
            onDeleteLocation={onDeleteLocation}
            disableCopy={!gebrek.locaties || gebrek.locaties.length === 0}
          />
          {/* --- End Locaties Section --- */}

          {/* --- Media Section (Gebrek) --- */}
          <MediaSection
            gebrek={gebrek}
            media={gebrek.media}
            uploadError={uploadError}
            onMediaUpload={handleMediaUploadInternal}
            onDeleteMedia={deleteMediaItem}
          />
          {/* --- End Media Section (Gebrek) --- */}

          {/* --- Gevaarlijke Situatie Section --- */}
          {!gebrek.gebrekid.startsWith("temp") && (
            <GevaarlijkeSituatieSection
              customerName={customerName}
              objectName={objectName}
              gebrek={gebrek}
              gsUploadErrors={gsUploadErrors}
              onUpdateGebrek={onUpdateGebrek}
              onSetGsUploadError={onSetGsUploadError}
              index={index}
              upload_image={upload_image}
              report_situatie={report_situatie}
            />
          )}
          {gebrek.gebrekid.startsWith("temp") && (
            <div className="bg-yellow-100 p-2 rounded-md">
              <small className="text-yellow-800">
                Sla eerst de inspectie op om een gevaarlijke situatie te melden!
              </small>
            </div>
          )}
          {/* --- End Gevaarlijke Situatie Section --- */}
        </div>
      )}
    </div>
  );
};

export default GebrekItem;
